import React from 'react';
import { Box, Typography, Chip, Paper, Link } from '@mui/material';
import moment from 'moment';
import { capitalize, phonePrint, limitStringLength } from '@pv/common/utils';
import { FraudTokenEventFieldsFragment } from '@pv/common/graphql/operations';

const maxMessageLength = 200;

const formatDate = (date: string) => {
  return moment(date).format('MMM D, YYYY');
};

export const FraudulentEventCard = ({
  event,
}: {
  event: FraudTokenEventFieldsFragment;
}) => {
  const firstPlannerMessage = event.messages.filter(
    (message: any) => message.sender === 'planner'
  )[0];
  const detectedFraudTokens = event.fraudDetections.map(
    (detection) => detection.fraudToken.value
  );
  const uniqueDetectedFraudTokens = [...new Set(detectedFraudTokens)];
  return (
    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Typography variant="body1" fontWeight="bold">
            {event.name || 'Untitled Event'}
          </Typography>
          <Chip label={`Event ID: ${event.id}`} size="small" />
        </Box>
        <Chip
          label={event.discardedAt ? 'Deleted' : capitalize(event.status)}
          size="small"
          color={
            event.discardedAt || event.status === 'lost' ? 'error' : 'default'
          }
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {uniqueDetectedFraudTokens.map((tokenValue) => (
          <Chip
            key={tokenValue}
            label={tokenValue}
            size="small"
            color="warning"
          />
        ))}
      </Box>
      <Typography variant="body2" color="text.secondary">
        Venue:{' '}
        <Link href={`/admin/venues/${event.venue?.id}`}>
          {event.venue.name}
        </Link>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Planner:{' '}
        {[
          event.planner?.fullName,
          event.planner?.email,
          phonePrint(event.planner?.phone),
        ]
          .filter(Boolean)
          .join(' | ')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Created: {formatDate(event.createdAt)}
        {event.markedFraudulentAt &&
          ` | Marked Fraudulent: ${formatDate(event.markedFraudulentAt)}`}
        {event.discardedAt && ` | Discarded: ${formatDate(event.discardedAt)}`}
      </Typography>

      {event.messages && event.messages.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            bgcolor: 'grey.50',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            First Planner Message:
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.5 }}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {limitStringLength(
              maxMessageLength,
              firstPlannerMessage?.plainTextFromHtml
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
